import * as Ably from 'ably';

import { NotificationApi } from '@/api';
import { INotification } from '@/models';

export interface NotificationServiceConfig {
  accountId: string;
  userId: string;
  onNotificationReceived: (notification: INotification) => void;
  onConnectionStatusChange?: (
    status: 'connected' | 'disconnected' | 'failed',
  ) => void;
  pollingInterval?: number; // in milliseconds, default 30 seconds
}

export class NotificationService {
  private ablyClient: Ably.Types.RealtimePromise | null = null;

  private channel: Ably.Types.RealtimeChannelPromise | null = null;

  private config: NotificationServiceConfig;

  private pollingTimer: NodeJS.Timeout | null = null;

  private isWebSocketConnected = false;

  private lastNotificationTimestamp: string | null = null;

  constructor(config: NotificationServiceConfig) {
    this.config = {
      ...config,
      pollingInterval: config.pollingInterval || 30000, // Default 30 seconds
    };
  }

  /**
   * Initialize the notification service with WebSocket connection
   */
  public async initialize(
    ablyClient: Ably.Types.RealtimePromise,
  ): Promise<void> {
    this.ablyClient = ablyClient;

    try {
      await this.setupWebSocketSubscription();
      this.setupConnectionMonitoring();
    } catch (error) {
      this.fallbackToPolling();
    }
  }

  /**
   * Subscribe to the account-specific notification channel
   */
  private async setupWebSocketSubscription(): Promise<void> {
    if (!this.ablyClient) {
      throw new Error('Ably client not initialized');
    }

    const channelName = `account:${this.config.accountId}:user:${this.config.userId}:notifications`;
    this.channel = this.ablyClient.channels.get(channelName);

    this.channel.on('failed', () => {
      this.isWebSocketConnected = false;
      this.config.onConnectionStatusChange?.('failed');
      this.fallbackToPolling();
    });

    this.channel.subscribe(
      'in-app-notification',
      (message: Ably.Types.Message) => {
        try {
          const notification = message.data as INotification;
          this.handleNotificationReceived(notification);
        } catch (error) {
          // Error processing notification
        }
      },
    );

    try {
      await this.channel.attach();
      this.isWebSocketConnected = true;
      this.config.onConnectionStatusChange?.('connected');
    } catch (error) {
      this.isWebSocketConnected = false;
      this.config.onConnectionStatusChange?.('failed');
      this.fallbackToPolling();
      throw error;
    }
  }

  /**
   * Monitor WebSocket connection status
   */
  private setupConnectionMonitoring(): void {
    if (!this.ablyClient) return;

    this.ablyClient.connection.on('connected', () => {
      this.isWebSocketConnected = true;
      this.config.onConnectionStatusChange?.('connected');
      this.stopPolling(); // Stop polling when WebSocket is connected
    });

    this.ablyClient.connection.on('disconnected', () => {
      this.isWebSocketConnected = false;
      this.config.onConnectionStatusChange?.('disconnected');
      this.fallbackToPolling();
    });

    this.ablyClient.connection.on('failed', () => {
      this.isWebSocketConnected = false;
      this.config.onConnectionStatusChange?.('failed');
      this.fallbackToPolling();
    });

    this.ablyClient.connection.on('suspended', () => {
      this.isWebSocketConnected = false;
      this.config.onConnectionStatusChange?.('disconnected');
      this.fallbackToPolling();
    });
  }

  /**
   * Handle received notification
   */
  private handleNotificationReceived(notification: INotification): void {
    // Update last notification timestamp for polling
    this.lastNotificationTimestamp =
      typeof notification.createdAt === 'string'
        ? notification.createdAt
        : new Date(notification.createdAt).toISOString();

    // Call the callback
    this.config.onNotificationReceived(notification);
  }

  /**
   * Fallback to API polling when WebSocket fails
   */
  private fallbackToPolling(): void {
    if (this.pollingTimer) return;

    this.pollingTimer = setInterval(async () => {
      if (this.isWebSocketConnected) {
        this.stopPolling();
        return;
      }

      try {
        await this.pollForNotifications();
      } catch (error) {
        // Error polling for notifications
      }
    }, this.config.pollingInterval);

    this.pollForNotifications();
  }

  /**
   * Poll for new notifications via API
   */
  private async pollForNotifications(): Promise<void> {
    try {
      const params: any = {
        sort: JSON.stringify({ createdAt: -1 }),
        limit: 10,
      };

      if (this.lastNotificationTimestamp) {
        params.filter = JSON.stringify({
          createdAt: { $gt: this.lastNotificationTimestamp },
        });
      }

      const response = await NotificationApi.getNotifications(params);
      const notifications = response.data.data;

      notifications.reverse().forEach((notification) => {
        this.handleNotificationReceived(notification);
      });
    } catch (error) {
      // Failed to poll notifications
    }
  }

  /**
   * Stop API polling
   */
  private stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<NotificationServiceConfig>): void {
    const oldAccountId = this.config.accountId;
    const oldUserId = this.config.userId;

    this.config = { ...this.config, ...newConfig };

    // If account or user changed, reinitialize subscription
    if (
      (newConfig.accountId && newConfig.accountId !== oldAccountId) ||
      (newConfig.userId && newConfig.userId !== oldUserId)
    ) {
      this.cleanup();
      if (this.ablyClient) {
        this.initialize(this.ablyClient);
      }
    }
  }

  /**
   * Get current connection status
   */
  public getConnectionStatus(): 'connected' | 'disconnected' | 'failed' {
    if (!this.ablyClient) return 'failed';

    const connectionState = this.ablyClient.connection.state;

    if (connectionState === 'connected') return 'connected';
    if (connectionState === 'failed') return 'failed';
    return 'disconnected';
  }

  /**
   * Check if WebSocket is currently connected
   */
  public isConnected(): boolean {
    return this.isWebSocketConnected;
  }

  /**
   * Manually trigger a notification poll (useful for testing)
   */
  public async triggerPoll(): Promise<void> {
    await this.pollForNotifications();
  }

  /**
   * Clean up resources
   */
  public cleanup(): void {
    // Unsubscribe from channel
    if (this.channel) {
      this.channel.unsubscribe();
      this.channel.detach();
      this.channel = null;
    }

    // Stop polling
    this.stopPolling();

    // Reset state
    this.isWebSocketConnected = false;
    this.lastNotificationTimestamp = null;
  }

  /**
   * Destroy the service completely
   */
  public destroy(): void {
    this.cleanup();
    this.ablyClient = null;
  }
}

// Singleton instance for global use
let notificationServiceInstance: NotificationService | null = null;

export const createNotificationService = (
  config: NotificationServiceConfig,
): NotificationService => {
  if (notificationServiceInstance) {
    notificationServiceInstance.destroy();
  }

  notificationServiceInstance = new NotificationService(config);
  return notificationServiceInstance;
};

export const getNotificationService = (): NotificationService | null => {
  return notificationServiceInstance;
};
